import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import emendaService, { resolveEmendaStatus } from '../services/emendaService';
import institutionService from '../services/institutionService';
import ConfirmActionModal from './ConfirmActionModal';
import ActionButton from './ui/ActionButton';
import PanelHeader from './ui/PanelHeader';
import StatusBadge, { type BadgeTone } from './ui/StatusBadge';
import Card from './ui/Card';
import { toUpperText } from '../utils/formatters';

interface Props {
  emendaId: string;
  emendaStatus?: string;
  tipoTransferencia?: string;
  canAct?: boolean;
  onStatusChange?: (newStatus: string) => void;
}

const ConveniosDocumentalPanel: React.FC<Props> = ({ emendaId, emendaStatus, tipoTransferencia, canAct = false, onStatusChange }) => {
  const queryClient = useQueryClient();
  const [parecer, setParecer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<{ acao: string; observacao?: string } | null>(null);

  const isIndireta = tipoTransferencia === 'Indireta';

  const acaoMutation = useMutation({
    mutationFn: (payload: { acao: string; observacao?: string }) =>
      emendaService.executarAcao(emendaId, { acao: payload.acao as any, observacao: payload.observacao }),
    onSuccess: async (updated, variables) => {
      setError(null); setParecer('');
      queryClient.invalidateQueries({ queryKey: ['historico', emendaId] });
      if (onStatusChange) onStatusChange(resolveEmendaStatus(updated));
      if (variables?.acao === 'REPROVAR_DOCUMENTAL') {
        try {
          const emendaDetails = await emendaService.getByIdWithDetails(emendaId);
          const instId = (emendaDetails as any).institutionId || (emendaDetails as any).institutionIdRef || null;
          if (instId) institutionService.moveToEndQueue(String(instId)).catch(() => {});
        } catch {}
      }
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? e?.response?.data?.error ?? 'Erro ao executar a ação'),
  });

  const isDemandaAprov      = emendaStatus === 'Análise de demanda aprovada';
  const isEmAnaliseDoc      = emendaStatus === 'Em análise documental';
  const isDocumentalAprov   = emendaStatus === 'Análise documental aprovada';
  const isDevolvida         = emendaStatus === 'Devolvida por inviabilidade documental';
  const jaFinalizado        = isDocumentalAprov || isDevolvida;
  const statusLabel         = isDocumentalAprov ? 'Aprovada' : isDevolvida ? 'Devolvida' : isEmAnaliseDoc ? 'Em análise' : null;
  const statusTone: BadgeTone = isDevolvida ? 'danger' : isDocumentalAprov ? 'success' : 'warning';

  const isRelevant = isDemandaAprov || isEmAnaliseDoc || isDocumentalAprov || isDevolvida;
  if (!isRelevant) return null;

  if (!isIndireta) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300">
        Execução direta — não há análise documental neste fluxo. A progressão para Iniciado é feita pelo setor responsável.
      </section>
    );
  }

  return (
    <Card>
      <PanelHeader title="Documental" owner="Convênios"
        right={statusLabel && <StatusBadge tone={statusTone}>{statusLabel}</StatusBadge>} />

      <div className="space-y-2 px-4 py-3">
        {!canAct && <p className="text-xs text-slate-500 dark:text-slate-400">Somente o setor de Convênios pode executar esta análise.</p>}

        {canAct && isDemandaAprov && (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-slate-100/70 px-4 py-6 text-center dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-sm text-slate-600 dark:text-slate-300">Demanda aprovada — pronta para a análise documental.</p>
            <ActionButton kind="neutral" size="md" onClick={() => acaoMutation.mutate({ acao: 'INICIAR_ANALISE_DOCUMENTAL' })}
              disabled={acaoMutation.isPending} loading={acaoMutation.isPending}>
              {acaoMutation.isPending ? 'Iniciando…' : 'Iniciar análise documental'}
            </ActionButton>
          </div>
        )}

        {canAct && isEmAnaliseDoc && (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-100/70 p-4 dark:border-slate-700 dark:bg-slate-900/40">
            <div>
              <label htmlFor="parecer-documental" className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">Parecer documental</label>
              <textarea
                id="parecer-documental"
                value={parecer}
                onChange={(e) => setParecer(toUpperText(e.target.value))}
                rows={3}
                placeholder="Descreva o parecer documental…"
                className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
            </div>
            <div className="flex justify-center gap-3 pt-1">
              <ActionButton kind="approve" size="md"
                onClick={() => { if (!parecer.trim()) { setError('Informe o parecer.'); return; } setError(null); setPendingPayload({ acao: 'APROVAR_DOCUMENTAL', observacao: parecer }); setConfirmOpen(true); }}
                disabled={acaoMutation.isPending} loading={acaoMutation.isPending && pendingPayload?.acao === 'APROVAR_DOCUMENTAL'}>
                Aprovar
              </ActionButton>
              <ActionButton kind="reject" variant="outline" size="md"
                onClick={() => { if (!parecer.trim()) { setError('Informe a justificativa.'); return; } setError(null); setPendingPayload({ acao: 'REPROVAR_DOCUMENTAL', observacao: parecer }); setConfirmOpen(true); }}
                disabled={acaoMutation.isPending} loading={acaoMutation.isPending && pendingPayload?.acao === 'REPROVAR_DOCUMENTAL'}>
                Reprovar
              </ActionButton>
            </div>
          </div>
        )}

        {canAct && jaFinalizado && (
          isDevolvida
            ? <p className="text-xs font-medium text-red-600 dark:text-red-400">Devolvida por inviabilidade documental. Veja a justificativa no histórico.</p>
            : <p className="text-xs text-slate-500 dark:text-slate-400">Análise concluída.</p>
        )}
        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>

      <ConfirmActionModal
        open={confirmOpen}
        title={pendingPayload?.acao === 'APROVAR_DOCUMENTAL' ? 'Confirmar aprovação' : 'Confirmar reprovação'}
        message={pendingPayload?.acao === 'APROVAR_DOCUMENTAL' ? 'Confirma a aprovação documental?' : 'Confirma a reprovação documental?'}
        confirmLabel={pendingPayload?.acao === 'APROVAR_DOCUMENTAL' ? 'Aprovar' : 'Reprovar'}
        cancelLabel="Cancelar"
        onConfirm={() => { if (pendingPayload) acaoMutation.mutate(pendingPayload); setConfirmOpen(false); }}
        onCancel={() => setConfirmOpen(false)}
        loading={acaoMutation.isPending}
      />
    </Card>
  );
};

export default ConveniosDocumentalPanel;
